/**
 * Sanitizes a caller-supplied redirect target that is allowed to leave the
 * application (QL-33 standalone flow links carry a `redirectUrl` back to the
 * external system that opened them).
 *
 * Unlike {@link sanitizeReturnTo} — which origin-locks the target to the app
 * itself — this helper deliberately accepts cross-origin destinations, but only
 * over `http:`/`https:`. The value is resolved against `base` with the WHATWG
 * URL API, so a relative path resolves to the app's own origin and stays valid,
 * while any scheme that could execute in the current document
 * (`javascript:`, `data:`, `vbscript:`, `blob:`, …) or otherwise escape the web
 * (`file:`, custom app schemes) returns `null`, as does empty or unparsable
 * input.
 *
 * The returned value is the normalized absolute `href`, safe to hand to
 * `window.location.assign`.
 *
 * @param redirectUrl The untrusted, caller-supplied redirect target.
 * @param base The trusted base to resolve relative values against.
 * @returns A normalized absolute http(s) URL, or `null` if rejected.
 */
export function sanitizeRedirectUrl(
  redirectUrl: string | null | undefined,
  base: string,
): string | null {
  if (typeof redirectUrl !== "string" || redirectUrl === "") {
    return null;
  }

  try {
    const resolved = new URL(redirectUrl, base);
    if (resolved.protocol !== "http:" && resolved.protocol !== "https:") {
      return null;
    }
    return resolved.href;
  } catch {
    return null;
  }
}
